import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Participant } from 'twilio-video';
import { Button, Snackbar, Paper, Typography, Box } from '@material-ui/core';
import Portal from '@material-ui/core/Portal';

import useParticipantConnectionWarning from '../../hooks/useParticipantConnectionWarning/useParticipantConnectionWarning';

let activeWarningOwner: symbol | null = null;

const useStyles = makeStyles(theme => ({
  snackbarRoot: {
    top: '24px',
    right: '24px',
    zIndex: 2147483647,

    [theme.breakpoints.down('sm')]: {
      top: '16px',
      left: '16px',
      right: '16px',
    },
  },

  floatingPaper: {
    borderRadius: '12px',
    padding: theme.spacing(2.5),
    width: '380px',
    maxWidth: 'calc(100vw - 32px)',
    backgroundColor: '#ffffff',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e2e8f0',

    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),

    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 32px)',
      maxWidth: 'none',
      padding: theme.spacing(3),
      borderRadius: '14px',
      gap: theme.spacing(2),
    },
  },

  title: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#1a202c',
    lineHeight: 1.3,

    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      lineHeight: 1.35,
    },
  },

  content: {
    fontSize: '0.9rem',
    color: '#4a5568',
    lineHeight: 1.5,

    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      lineHeight: 1.55,
    },
  },

  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },

  button: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    padding: '8px 16px',

    [theme.breakpoints.down('sm')]: {
      padding: '10px 18px',
      fontSize: '0.95rem',
    },
  },

  closeButton: {
    textTransform: 'none',
    fontWeight: 600,
    color: '#718096',
    padding: '8px',
    minWidth: 'auto',

    [theme.breakpoints.down('sm')]: {
      padding: '10px',
      fontSize: '0.95rem',
    },
  },
}));

interface ConnectionWarningModalProps {
  participant: Participant;
  onActionClick?: () => void;
}

export default function ConnectionWarningModal({ participant }: ConnectionWarningModalProps) {
  const classes = useStyles();

  const { isLowQuality } = useParticipantConnectionWarning({ participant });

  const [dismissed, setDismissed] = useState(true);
  const [blocked, setBlocked] = useState(false);

  const warningOwnerRef = useRef(Symbol());

  useEffect(() => {
    if (!isLowQuality) {
      setDismissed(false);
      return;
    }

    if (dismissed) {
      const cooldownTimer = setTimeout(() => {
        setDismissed(false);
      }, 30000);

      return () => clearTimeout(cooldownTimer);
    }
  }, [isLowQuality, dismissed]);

  useEffect(() => {
    const shouldOpen = isLowQuality && !dismissed;

    if (!shouldOpen) {
      return;
    }

    if (activeWarningOwner !== null && activeWarningOwner !== warningOwnerRef.current) {
      setBlocked(true);
      return;
    }

    activeWarningOwner = warningOwnerRef.current;
    setBlocked(false);

    return () => {
      if (activeWarningOwner === warningOwnerRef.current) {
        activeWarningOwner = null;
      }
    };
  }, [isLowQuality, dismissed]);

  const showWarning = isLowQuality && !dismissed && !blocked;

  const handleClose = () => {
    setDismissed(true);

    if (activeWarningOwner === warningOwnerRef.current) {
      activeWarningOwner = null;
    }
  };

  return (
    <Portal>
      <Snackbar
        open={showWarning}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={classes.snackbarRoot}
      >
        <Paper className={classes.floatingPaper} elevation={0}>
          <Typography className={classes.title}>Detectamos instabilidade na conexão de internet</Typography>

          <Typography className={classes.content}>
            A chamada está sofrendo com uma internet instável — pode ser a sua conexão ou a do outro participante, não
            um problema da nossa plataforma. Se a instabilidade for do seu lado, tente fechar outras abas ou aplicativos
            que estejam usando a internet e se aproxime do roteador. Se o problema persistir, você pode falar com o
            nosso suporte.
          </Typography>

          <Box className={classes.actions}>
            <Button className={classes.closeButton} onClick={handleClose}>
              Fechar
            </Button>

            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => {
                const phoneNumber = '551142803624';
                const message = 'Olá, estou com problemas de conexão durante a teleconsulta.';

                window.open(
                  `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              Preciso de ajuda
            </Button>
          </Box>
        </Paper>
      </Snackbar>
    </Portal>
  );
}
